/**
 * Layout Constants
 *
 * Centralized layout constants for consistent spacing, sizing, and z-indexing
 * across the Sphero BPS application.
 */

export const LAYOUT_CONSTANTS = {
  // Heights
  PRIMARY_BAR_HEIGHT: 64,
  BOTTOM_CONTROL_BAR_HEIGHT: 48,
  BOTTOM_DRAWER_STATUS_BAR_HEIGHT: 40,
  BOTTOM_DRAWER_EXPANDED_HEIGHT: 250,
  MONACO_TAB_HEADER_HEIGHT: 40,

  // Zoom defaults (shared across pages)
  ZOOM: {
    MIN: 0.5,
    MAX: 2,
    STEP: 0.1,
  },

  // Drawer widths
  // Shared left tab rail width for all persistent left drawers
  LEFT_DRAWER_TAB_RAIL_WIDTH: 94,
  // Default content widths for left drawers (per page)
  LEFT_DRAWER_DEFAULTS: {
    CODE_EDITOR: 375,
    DASHBOARD: 300,
    PATH_BUILDER: 475,
  } as const,
  // Per-page limits for resizable left drawers
  // Returns dynamic limits based on screen width
  LEFT_DRAWER_LIMITS: {
    CODE_EDITOR: {
      get MIN() {
        return Math.floor(window.innerWidth / 5);
      },
      get MAX() {
        return Math.floor((2 * window.innerWidth) / 4);
      },
    },
    DASHBOARD: {
      get MIN() {
        return Math.floor(window.innerWidth / 6);
      },
      get MAX() {
        return Math.floor(window.innerWidth / 3);
      },
    },
    PATH_BUILDER: {
      get MIN() {
        return Math.floor(window.innerWidth / 3);
      },
      get MAX() {
        return Math.floor((2 * window.innerWidth) / 4);
      },
    },
  },
  // Convenience: total = rail + content
  LEFT_DRAWER_TOTAL_WIDTH(contentWidth: number): number {
    return this.LEFT_DRAWER_TAB_RAIL_WIDTH + contentWidth;
  },

  // Z-indices
  Z_INDEX: {
    PRIMARY_BAR: 1100,
    DRAWER: 1200,
    BOTTOM_DRAWER: 1198,
    BOTTOM_CONTROL_BAR: 1199,
    PULL_TAB: 1201,
    // Above BPS panel chrome and MUI modal defaults so fixed Monaco popups remain visible.
    EDITOR_POPUP: 1301,
    DRAG_OVERLAY: 1500,
  },

  // Transitions
  DRAWER_TRANSITION_DURATION: 225, // ms
  DRAWER_TRANSITION_EASING: 'cubic-bezier(0.4, 0, 0.2, 1)',

  // Page navigation tab spacing
  PAGE_NAV_TAB_SPACING: 4,

  /** Primary app bar responsive compaction (nav labels, read-only banner). */
  PRIMARY_BAR: {
    /** Minimum width reserved for the centered project title when not yet measured. */
    MIN_TITLE_WIDTH: 96,
    /** Edit pencil + spacing reserved beside the measured title width. */
    TITLE_EDIT_BUTTON_WIDTH: 36,
    /** Horizontal padding around the centered title column (`px: 1` × 2). */
    TITLE_CENTER_PADDING: 16,
    /** Extra row width required before expanding from icon-only back to labels. */
    COMPACT_TO_FULL_HYSTERESIS_PX: 16,
    /** Fallback when labeled left chrome has not been measured yet. */
    LABELED_LEFT_FALLBACK_PX: 420,
    /** Fallback when full right chrome has not been measured yet. */
    FULL_RIGHT_FALLBACK_PX: 280,
    /** Compact viewer info icon width used when swapping in the full read-only banner. */
    COMPACT_VIEWER_INFO_ICON_WIDTH_PX: 40,
  },

  // Path Builder responsive behavior
  /** Matches PathBuilder main `Stack` horizontal padding (`px={5}` × 2 at spacing 8). */
  PATH_BUILDER_MAIN_HORIZONTAL_PADDING: 80,
  /**
   * When the drawer is at its design minimum width, reserve this much for the field column
   * before auto-collapsing the drawer to the tab rail.
   */
  PATH_BUILDER_MIN_MAIN_AREA_WITH_DRAWER_AT_MIN: 280,
} as const;

/**
 * True when the viewport cannot fit tab rail + drawer at PATH_BUILDER min width
 * + main area horizontal padding + minimum field strip.
 */
export function shouldAutoCollapsePathBuilderDrawer(viewportWidth: number): boolean {
  const rail = LAYOUT_CONSTANTS.LEFT_DRAWER_TAB_RAIL_WIDTH;
  const gutter = LAYOUT_CONSTANTS.PATH_BUILDER_MAIN_HORIZONTAL_PADDING;
  const minMain = LAYOUT_CONSTANTS.PATH_BUILDER_MIN_MAIN_AREA_WITH_DRAWER_AT_MIN;
  const drawerMinContent = Math.floor(viewportWidth / 3);
  const minRequired = rail + drawerMinContent + gutter + minMain;
  return viewportWidth < minRequired;
}
